import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flag, ToggleLeft, ToggleRight, RefreshCcw } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFeatureFlags = () => {
  const { user } = useAuth();
  const isPlatformOwner = user?.platform_role === 'platform_owner';
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    try {
      const response = await axios.get(`${API}/admin/feature-flags`);
      setFlags(response.data.flags || []);
    } catch (error) {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async (flagName, currentlyEnabled) => {
    if (!isPlatformOwner) {
      toast.error('Only Platform Owner can toggle feature flags');
      return;
    }

    const reason = prompt('Please provide a reason for this change (min 10 characters):');
    if (!reason || reason.length < 10) {
      toast.error('Reason is required (min 10 characters)');
      return;
    }

    try {
      await axios.patch(`${API}/admin/feature-flags/${flagName}?enabled=${!currentlyEnabled}&reason=${encodeURIComponent(reason)}`);
      toast.success(`Feature flag ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
      fetchFlags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle flag');
    }
  };
  
  const handleResetDemoData = async () => {
    if (!isPlatformOwner) {
      toast.error('Only Platform Owner can reset demo data');
      return;
    }
    
    const confirm = window.confirm(
      'Are you sure you want to reset demo data? This will delete all demo data and reseed it.'
    );
    if (!confirm) return;
    
    setResetting(true);
    try {
      await axios.post(`${API}/admin/demo-data/reset`);
      toast.success('Demo data reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset demo data');
    } finally {
      setResetting(false);
    }
  };
  
  const isDemoModeEnabled = flags.find(f => f.flag_name === 'DEMO_MODE')?.enabled || false;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Feature Flags</h1>
            <p className="text-muted-foreground">Control platform features</p>
          </div>
          <div className="flex gap-2">
            {!isPlatformOwner && (
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                Read-Only (Platform Owner required)
              </Badge>
            )}
            {isPlatformOwner && isDemoModeEnabled && (
              <button
                onClick={handleResetDemoData}
                disabled={resetting}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCcw className="w-4 h-4" />
                {resetting ? 'Resetting...' : 'Reset Demo Data'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">Loading flags...</div>
          ) : flags.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">No feature flags found</div>
          ) : (
            flags.map((flag) => (
              <Card key={flag.flag_name} className="border-2 border-border">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg text-foreground">{flag.flag_name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{flag.description}</p>
                    </div>
                    <button
                      onClick={() => toggleFlag(flag.flag_name, flag.enabled)}
                      disabled={!isPlatformOwner}
                      className="p-2 hover:bg-muted rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {flag.enabled ? (
                        <ToggleRight className="w-8 h-8 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-400" />
                      )}
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Badge className={flag.enabled 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                    }>
                      {flag.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  {flag.updated_at && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Last updated: {new Date(flag.updated_at).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFeatureFlags;
